import { Link } from "wouter";
import { Building2, ArrowLeft } from "lucide-react";
import { SEOHead } from "@/components/seo-head";

const PRIVACY_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy",
    "description": "The Proper Tool privacy policy: how we collect, use, and protect your personal data under UK GDPR.",
    "url": "https://thepropertool.com/privacy",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://thepropertool.com",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Privacy Policy",
        "item": "https://thepropertool.com/privacy",
      },
    ],
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Privacy Policy"
        description="The Proper Tool privacy policy: how we collect, use, and protect your personal data under UK GDPR."
        canonical="/privacy"
        schema={PRIVACY_SCHEMA}
      />
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-bold">The Proper Tool</span>
            </div>
          </Link>
          <Link href="/">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-10">Last updated: June 2026</p>

        <div className="prose prose-sm max-w-none space-y-8 text-foreground">

          <section>
            <h2 className="text-xl font-semibold mb-3">1. Who We Are</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Proper Tool is operated by The Proper Tool Ltd ("we", "us", "our"). We are the data controller for personal data collected through this platform. If you have any questions about how we handle your data, please contact us at hello@thepropertool.com.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              This policy explains what personal data we collect, why we collect it, how we use it, and your rights under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. What Personal Data We Collect</h2>
            <p className="text-muted-foreground leading-relaxed">We collect the following categories of data:</p>

            <div className="mt-4 space-y-4">
              <div>
                <h3 className="font-medium text-foreground">Account Information</h3>
                <p className="text-muted-foreground mt-1 leading-relaxed">Your name, email address, company name, and password (stored as a secure hash). If you sign in using Google, we receive your name, email and profile picture from Google.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground">Business and Project Data</h3>
                <p className="text-muted-foreground mt-1 leading-relaxed">Construction project details, client names and contact information, quote contents (including line items, quantities and pricing), cost library entries, and pricing data that you enter into the platform. This also includes any documents, drawings, photographs or other files you upload to the platform (such as structural drawings or site survey attachments). As the platform operator, The Proper Tool Ltd may access this data for the purposes described in Section 3 below.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground">Usage Data</h3>
                <p className="text-muted-foreground mt-1 leading-relaxed">Information about how you use the platform, including pages visited, features used, and quote generation activity. This helps us improve the service.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground">Payment Information</h3>
                <p className="text-muted-foreground mt-1 leading-relaxed">We do not store your card details. Payment processing is handled by Stripe, who hold your payment information securely in accordance with their own privacy policy. We receive confirmation of payment status and subscription details from Stripe.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground">Communications</h3>
                <p className="text-muted-foreground mt-1 leading-relaxed">Any messages you send to us via support channels or feedback forms.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. Why We Use Your Data and Our Legal Basis</h2>

            <div className="mt-2 space-y-4">
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted">
                      <th className="text-left px-4 py-3 font-medium text-foreground">Purpose</th>
                      <th className="text-left px-4 py-3 font-medium text-foreground">Legal Basis</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="px-4 py-3 text-muted-foreground">Providing and operating the service</td>
                      <td className="px-4 py-3 text-muted-foreground">Performance of a contract</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-muted-foreground">Processing payments and managing subscriptions</td>
                      <td className="px-4 py-3 text-muted-foreground">Performance of a contract</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-muted-foreground">Sending service-related notifications and updates</td>
                      <td className="px-4 py-3 text-muted-foreground">Performance of a contract / Legitimate interests</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-muted-foreground">Improving the platform and AI models</td>
                      <td className="px-4 py-3 text-muted-foreground">Legitimate interests</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-muted-foreground">Internal review of quote content, descriptions, line items and uploaded files by authorised team members to identify errors, improve AI accuracy, test new features, and ensure quality</td>
                      <td className="px-4 py-3 text-muted-foreground">Legitimate interests</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-muted-foreground">Customer support, security investigation, abuse and fraud detection</td>
                      <td className="px-4 py-3 text-muted-foreground">Legitimate interests</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-muted-foreground">Complying with legal and regulatory obligations</td>
                      <td className="px-4 py-3 text-muted-foreground">Legal obligation</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-muted-foreground">Preventing fraud and ensuring platform security</td>
                      <td className="px-4 py-3 text-muted-foreground">Legitimate interests</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-muted-foreground">Sending marketing communications (if opted in)</td>
                      <td className="px-4 py-3 text-muted-foreground">Consent</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. AI Processing</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Proper Tool uses OpenAI's API to generate construction quote estimates. When you create an Auto quote, the project description and relevant context you provide are sent to OpenAI for processing. OpenAI processes this data in accordance with their data processing agreement with us and their own privacy policy.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Project descriptions and any uploaded documents you provide are sent to OpenAI solely to generate quote estimates. We do not intentionally include your account credentials or contact details in these requests. However, project descriptions and uploaded files may contain project-specific context such as site addresses or structural specifications if you have included them. You should be aware of this when composing descriptions or uploading documents.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              OpenAI does not use data submitted via the API to train their models unless you separately opt in to their data sharing programmes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Third-Party Service Providers</h2>
            <p className="text-muted-foreground leading-relaxed">We use the following third-party services to deliver the platform. Each acts as a data processor on our behalf and is bound by appropriate data processing agreements:</p>

            <div className="mt-4 space-y-3">
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium text-foreground text-sm">Stripe</p>
                <p className="text-muted-foreground text-sm mt-1">Payment processing. Stripe handles all payment card data and subscription billing. Stripe is PCI-DSS compliant. See stripe.com/gb/privacy.</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium text-foreground text-sm">OpenAI</p>
                <p className="text-muted-foreground text-sm mt-1">Auto quote generation. Project descriptions are sent to OpenAI's API to generate cost estimates. See openai.com/privacy.</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium text-foreground text-sm">Google (OAuth)</p>
                <p className="text-muted-foreground text-sm mt-1">If you use Sign in with Google, Google provides your name, email and profile picture to us. See policies.google.com/privacy.</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium text-foreground text-sm">Replit</p>
                <p className="text-muted-foreground text-sm mt-1">Cloud infrastructure and hosting. The platform is hosted on Replit's infrastructure. Replit processes data in accordance with their data processing agreement and privacy policy. See replit.com/privacy.</p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed mt-4">
              We do not sell your personal data to any third party.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. How Long We Keep Your Data</h2>
            <p className="text-muted-foreground leading-relaxed">We retain your data for as long as your account is active or as required to provide the service. Specifically:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-muted-foreground">
              <li>Account and subscription data is retained for the duration of your account plus 6 years to meet our legal and tax obligations.</li>
              <li>Quote and project data is retained for as long as your account is active. You can delete individual quotes at any time.</li>
              <li>If you close your account, we will delete or anonymise your personal data within 30 days, unless we are required to retain it by law.</li>
              <li>Uploaded documents and files (such as PDFs or drawings) are retained while your account is active and deleted within 30 days of account closure. You can delete individual uploads at any time.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Data Transfers</h2>
            <p className="text-muted-foreground leading-relaxed">
              Some of our third-party providers, including OpenAI and Stripe, may process data outside the UK. Where this happens, we ensure appropriate safeguards are in place, including reliance on adequacy decisions or standard contractual clauses as required by UK GDPR.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Your Rights Under UK GDPR</h2>
            <p className="text-muted-foreground leading-relaxed">You have the following rights regarding your personal data:</p>
            <ul className="mt-3 space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2"><span className="font-medium text-foreground min-w-fit">Right of access:</span> <span>You can request a copy of the personal data we hold about you.</span></li>
              <li className="flex items-start gap-2"><span className="font-medium text-foreground min-w-fit">Right to rectification:</span> <span>You can ask us to correct inaccurate or incomplete data.</span></li>
              <li className="flex items-start gap-2"><span className="font-medium text-foreground min-w-fit">Right to erasure:</span> <span>You can request that we delete your personal data, subject to legal retention requirements.</span></li>
              <li className="flex items-start gap-2"><span className="font-medium text-foreground min-w-fit">Right to portability:</span> <span>You can request your data in a structured, machine-readable format.</span></li>
              <li className="flex items-start gap-2"><span className="font-medium text-foreground min-w-fit">Right to restrict processing:</span> <span>You can ask us to pause processing in certain circumstances.</span></li>
              <li className="flex items-start gap-2"><span className="font-medium text-foreground min-w-fit">Right to object:</span> <span>You can object to processing based on legitimate interests.</span></li>
              <li className="flex items-start gap-2"><span className="font-medium text-foreground min-w-fit">Right to withdraw consent:</span> <span>Where processing is based on consent, you can withdraw it at any time.</span></li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              To exercise any of these rights, please contact us at hello@thepropertool.com. We will respond within 30 days. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use cookies to keep you logged in and to ensure the platform functions correctly. Specifically:
            </p>
            <div className="mt-4 space-y-3">
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium text-foreground text-sm">Session cookies (essential)</p>
                <p className="text-muted-foreground text-sm mt-1">Used to maintain your login session. These are strictly necessary for the platform to function and cannot be disabled.</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium text-foreground text-sm">Preference cookies</p>
                <p className="text-muted-foreground text-sm mt-1">Used to remember your settings such as dark mode preference. Stored in your browser's local storage.</p>
              </div>
            </div>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We do not use advertising or tracking cookies. We do not use any third-party analytics services that set cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We take the security of your data seriously. The platform uses HTTPS encryption for all data in transit. Passwords are stored as bcrypt hashes and are never stored in plain text. We do not store payment card details. Access to production data is restricted to authorised personnel only.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this privacy policy from time to time. Where changes are material, we will notify you by email or via an in-app notification. The updated policy will show the revised date at the top.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              For any questions about this privacy policy or to exercise your data rights, please contact us:
            </p>
            <div className="mt-3 p-4 bg-muted rounded-lg text-muted-foreground text-sm">
              <p className="font-medium text-foreground">The Proper Tool</p>
              <p>Email: hello@thepropertool.com</p>
              <p>Website: thepropertool.com</p>
            </div>
            <p className="text-muted-foreground leading-relaxed mt-4">
              You can also contact the Information Commissioner's Office if you have a concern about our use of your data: ico.org.uk / 0303 123 1113.
            </p>
          </section>

        </div>
      </main>

      <footer className="border-t mt-16 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} The Proper Tool. All rights reserved.</span>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link href="/cookies" className="hover:text-foreground transition-colors">Cookie Policy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms and Conditions</Link>
            <Link href="/referral-terms" className="hover:text-foreground transition-colors">Referral Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
